/**
 * Shared API client for every CFO page.
 *
 * Keep requests relative to /api: Vite proxies them in development and the
 * application host resolves them in deployed environments. Page modules should
 * import `api` rather than call fetch directly.
 */
const API_BASE = "/api";

export class ApiError extends Error {
  constructor(message, { status = 0, data = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

function messageFrom(data, fallback) {
  const detail = data?.detail ?? data?.message ?? data?.erro;
  if (Array.isArray(detail)) return detail.map((entry) => entry.msg || entry.message).filter(Boolean).join("; ") || fallback;
  return typeof detail === "string" ? detail : fallback;
}

export async function request(path, options = {}) {
  const { body, headers, signal, ...rest } = options;
  const isForm = body instanceof FormData || body instanceof URLSearchParams || typeof body === "string";
  const response = await fetch(path.startsWith("/") ? path : `${API_BASE}/${path}`, {
    credentials: "include",
    // Dados financeiros e filtros de competência devem refletir a base atual;
    // uma resposta HTTP antiga não pode sobreviver à troca de dump local.
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(body && !isForm ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body: body && !isForm ? JSON.stringify(body) : body,
    signal,
    ...rest,
  });

  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json") ? await response.json().catch(() => null) : await response.text().catch(() => null);
  if (!response.ok) throw new ApiError(messageFrom(data, "Não foi possível concluir esta ação."), { status: response.status, data });
  return data;
}

export const api = {
  get: (path, options) => request(path, options),
  post: (path, body, options = {}) => request(path, { ...options, method: "POST", body }),
  put: (path, body, options = {}) => request(path, { ...options, method: "PUT", body }),
  patch: (path, body, options = {}) => request(path, { ...options, method: "PATCH", body }),
  remove: (path, options = {}) => request(path, { ...options, method: "DELETE" }),
  auth: {
    config: () => request("/api/auth/config"),
    me: () => request("/api/auth/me"),
    loginLocal: (email) => request("/api/auth/local/login", { method: "POST", body: { email } }),
    logout: () => request("/api/auth/logout", { method: "POST" }),
    ssoLoginUrl: "/api/auth/sso/login",
  },
};

export const formatters = {
  currency: (value, options = {}) => new Intl.NumberFormat("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 0, ...options,
  }).format(Number(value || 0)),
  number: (value, options = {}) => new Intl.NumberFormat("pt-BR", options).format(Number(value || 0)),
  percent: (value, options = {}) => new Intl.NumberFormat("pt-BR", {
    style: "percent", maximumFractionDigits: 1, ...options,
  }).format(Number(value || 0)),
  date: (value, options = {}) => new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit", month: "short", year: "numeric", ...options,
  }).format(new Date(value)),
};
